import React, { useState } from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import DemoVideoModal from "./DemoVideoModal";

const benefitItems = [
  {
    title: "They feel confident committing to treatment",
    content: "even high-value implant and All-on-X cases",
  },
  {
    title: "They accept faster",
    content: "often during the same consultation",
  },
  {
    title: "They stop price-shopping",
    content:
      "because they're emotionally invested in a specific result they've already seen",
  },
];

export default function CaseAcceptanceIncrease() {
  const [activeIdx, setActiveIdx] = useState(null);
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  const handleToggle = (idx) => {
    setActiveIdx(activeIdx === idx ? null : idx);
  };

  /* Figma-matched styles */
  const badgeBg = {
    background:
      "radial-gradient(50.06% 38.74% at 50% 50%, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 100%), rgba(15,111,222,0.14)",
    boxShadow:
      "0px 0.486px 1.459px rgba(10,124,255,0.24), 0px 7.783px 15.566px -3.891px rgba(10,124,255,0.48), inset 0.486px 0.486px 0.973px rgba(255,255,255,0.24)",
    borderRadius: 23,
  };
  const badgeText = {
    background:
      "radial-gradient(50.06% 38.74% at 50% 50%, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 100%), #0F6FDE",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    fontWeight: 600,
    fontSize: 17,
    lineHeight: "140%",
  };
  const imgBefore = {
    border: "10px solid rgba(15,111,222,0.14)",
    boxShadow: "0px 3.8px 69.6px rgba(0,0,0,0.08)",
    backdropFilter: "blur(50.7px)",
    borderRadius: 24,
  };
  const imgGlow = {
    border: "10px solid rgba(15,111,222,0.14)",
    boxShadow:
      "0px 0.63px 1.9px rgba(10,124,255,0.24), 0px 3.8px 69.6px rgba(0,0,0,0.08), 0px 2.53px 5.06px rgba(10,124,255,0.12), 0px 4.23px 20.25px 1.99px rgba(10,124,255,0.48), inset 0.63px 0.63px 1.27px rgba(255,255,255,0.24)",
    backdropFilter: "blur(50.7px)",
    borderRadius: 24,
  };

  return (
    <section className="relative py-16 md:py-20 px-3 sm:px-4 lg:px-6 max-w-7xl mx-auto overflow-visible">
      <div className="rounded-[36px] border border-slate-200/90 bg-white shadow-lg px-5 py-6 sm:px-7 sm:py-8 lg:px-10 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,520px)_minmax(0,1fr)] gap-12 lg:gap-12 items-center">
          {/* Left panel — image composition */}
          <div className="relative flex items-center justify-center w-full">
            <div className="relative mx-auto w-full max-w-[580px] aspect-[580/560]">
              <div
                className="absolute inset-0 rounded-[28px] overflow-hidden border border-slate-100"
                style={{ backgroundColor: "#f8fafc" }}
              >
                <div
                  className="absolute inset-0 opacity-[0.45] pointer-events-none"
                  style={{
                    backgroundImage:
                      "linear-gradient(#cbd5e1 1px, transparent 1px), linear-gradient(90deg, #cbd5e1 1px, transparent 1px)",
                    backgroundSize: "28px 28px",
                    maskImage:
                      "radial-gradient(ellipse at center, black 38%, transparent 92%)",
                  }}
                />
              </div>

              <div className="absolute z-20 pointer-events-none flex flex-col items-end top-[10%] sm:top-[15%] left-[20%]">
                <span
                  className="text-[14px] md:text-[20px] lg:text-[28px]"
                  style={{
                    fontWeight: 700,
                    lineHeight: "1.1",
                    color: "#0E65CA",
                  }}
                >
                  60%+
                </span>
                <span
                  className="text-[12px] md:text-[14px] lg:text-[16px]"
                  style={{
                    fontWeight: 500,
                    lineHeight: "1.35",
                    color: "#475467",
                  }}
                >
                  Case Acceptance Rate
                </span>
              </div>

              <img
                src="/svgs/hero_arrow.svg"
                alt=""
                aria-hidden="true"
                className="absolute z-30 pointer-events-none w-[20%] top-[2%] left-[16%]"
                style={{ transform: "rotate(-55deg)" }}
              />

              <img
                src="/svgs/hero_arrow.svg"
                alt=""
                aria-hidden="true"
                className="absolute z-30 pointer-events-none w-[20%] top-[60%] right-[-4%]"
                style={{ transform: "rotate(95deg)" }}
              />

              <div className="absolute z-20 flex flex-col items-center gap-2 top-[24.5%] left-[2%] w-[37.5%]">
                <span
                  className="inline-flex items-center justify-center px-3 py-1"
                  style={badgeBg}
                >
                  <span
                    style={{
                      ...badgeText,
                      fontSize: "clamp(13px, 2.5vw, 17px)",
                    }}
                  >
                    Before
                  </span>
                </span>
                <img
                  src="/before_hero.jpg"
                  alt="Patient before treatment"
                  className="w-full aspect-square object-cover"
                  style={imgBefore}
                />
              </div>

              <div className="absolute z-20 pointer-events-none top-[74.5%] left-[2%] w-[32%]">
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: "clamp(11px, 2.25vw, 14px)",
                    lineHeight: "1.45",
                    letterSpacing: "0.01em",
                    color: "rgba(0,0,0,0.7)",
                  }}
                >
                  AI Smile Preview
                  <br />
                  in Seconds
                </span>
              </div>

              <div className="absolute z-20 flex flex-col items-center gap-2 top-[8.5%] left-[54%] w-[37.5%]">
                <span
                  className="inline-flex items-center justify-center px-3 py-1"
                  style={badgeBg}
                >
                  <span
                    style={{
                      ...badgeText,
                      fontSize: "clamp(13px, 2.5vw, 17px)",
                    }}
                  >
                    After
                  </span>
                </span>
                <img
                  src="/after_hero.jpg"
                  alt="Patient after treatment"
                  className="w-full aspect-square object-cover"
                  style={imgGlow}
                />
              </div>

              <div className="absolute z-20 flex flex-col items-center gap-2 top-[61%] left-[44%] w-[37.5%]">
                <div className="relative w-full">
                  <img
                    src="/video_hero.webp"
                    alt="AI Video preview"
                    className="w-full aspect-square object-cover"
                    style={imgGlow}
                  />
                  <div
                    className="absolute flex items-center justify-center pointer-events-none"
                    style={{
                      width: "16%",
                      aspectRatio: "1 / 1",
                      right: "6%",
                      bottom: "6%",
                      transform: "rotate(-2.26deg)",
                    }}
                  >
                    <div className="flex h-full w-full items-center justify-center rounded-full bg-white shadow-md">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-[44%] w-[44%] fill-current ml-[8%]"
                        style={{ color: "#1e293b" }}
                      >
                        <polygon points="5,3 19,12 5,21" />
                      </svg>
                    </div>
                  </div>
                </div>
                <span
                  className="inline-flex items-center justify-center px-3 py-1"
                  style={badgeBg}
                >
                  <span
                    style={{
                      ...badgeText,
                      fontSize: "clamp(13px, 2.5vw, 17px)",
                    }}
                  >
                    AI Video
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className="max-w-xl lg:max-w-none lg:pl-2 relative z-20">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              Dental Case Acceptance Rates Increase to 60%+ with AI Simulation
            </h2>

            <p className="mt-5 text-slate-500 text-base sm:text-lg leading-relaxed">
              SmileReveal™ removes the biggest barrier to treatment acceptance
              in dentistry: uncertainty about the final result.
            </p>

            <p className="mt-5 text-slate-900 text-lg sm:text-xl font-semibold leading-snug">
              When dental implant and cosmetic patients see a realistic preview
              of their outcome:
            </p>

            <div className="mt-5 space-y-3">
              {benefitItems.map((item, idx) => (
                <div
                  key={idx}
                  className="group flex flex-col gap-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 transition-all duration-300 hover:shadow-md cursor-pointer"
                  onMouseEnter={() => {
                    if (window.innerWidth >= 1024) setActiveIdx(idx);
                  }}
                  onMouseLeave={() => {
                    if (window.innerWidth >= 1024) setActiveIdx(null);
                  }}
                  onClick={() => handleToggle(idx)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ArrowRight className="w-5 h-5 text-brand-600 shrink-0" />
                      <span className="text-slate-900 font-bold text-sm sm:text-base leading-snug">
                        {item.title}
                      </span>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${activeIdx === idx ? "rotate-180 text-brand-600" : ""
                        }`}
                    />
                  </div>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${activeIdx === idx
                      ? "max-h-20 opacity-100 mt-2"
                      : "max-h-0 opacity-0"
                      }`}
                  >
                    <p className="text-slate-600 text-sm sm:text-base pl-8 border-l-2 border-brand-200 ml-2.5">
                      {item.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setIsDemoOpen(true)}
              className="mt-7 inline-flex items-center justify-center rounded-full border border-slate-300 bg-slate-100 px-8 py-3.5 text-slate-900 text-base font-semibold hover:bg-white transition-colors"
            >
              Watch Demo
            </button>
          </div>
        </div>
      </div>

      <DemoVideoModal isOpen={isDemoOpen} onClose={() => setIsDemoOpen(false)} />
    </section>
  );
}
